/**
 * AttachmentButton Component
 *
 * This component provides file attachment functionality for the chat input.
 * It now integrates with the backend file upload API.
 *
 * File uploads are sent to the session's upload endpoint and stored as artifacts.
 */

import { useState, useRef, useCallback } from "react";
import { useAppStore } from "@/lib/store";
import { Paperclip, X, File, Image as ImageIcon, Check, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import { api } from "../../lib/api/client";
import type { FileAttachment, AttachmentButtonProps } from "./types";

export function AttachmentButton({
  attachments,
  onAttachmentsChange,
  disabled = false,
}: AttachmentButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const selectedSessionId = useAppStore((s) => s.selectedSessionId);

  const getAttachmentType = (file: File): FileAttachment['type'] => {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.includes('pdf') || file.type.includes('document')) return 'document';
    if (file.type.includes('javascript') || file.type.includes('json') || file.type.includes('text')) return 'code';
    return 'file';
  };

  /**
   * Upload file to the backend API
   *
   * @param file - The file to upload
   * @returns Promise with upload response
   */
  const uploadFile = useCallback(async (file: File): Promise<FileAttachment> => {
    if (!selectedSessionId) {
      throw new Error("No session selected. Please select a session first.");
    }

    // Determine file type
    const fileType = file.name.endsWith(".csv") ? "csv" : "excel";

    // Call the upload API
    const response = await api.uploadFile(selectedSessionId, file, fileType);

    // Create attachment from server response
    const attachment: FileAttachment = {
      id: `uploaded-${Date.now()}-${Math.random()}`,
      file,
      type: getAttachmentType(file),
      name: file.name,
      size: file.size,
      status: 'uploaded',
      url: response.file_path,
    };

    return attachment;
  }, [selectedSessionId]);

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newAttachments: FileAttachment[] = [];

    for (const file of Array.from(files)) {
      // Create pending attachment
      const pendingAttachment: FileAttachment = {
        id: `pending-${Date.now()}-${Math.random()}`,
        file,
        type: getAttachmentType(file),
        name: file.name,
        size: file.size,
        status: 'uploading',
      };

      // Add to attachments immediately (optimistic UI)
      newAttachments.push(pendingAttachment);

      try {
        // Attempt upload
        const uploaded = await uploadFile(file);
        // Replace pending with uploaded
        const index = newAttachments.indexOf(pendingAttachment);
        if (index !== -1) {
          newAttachments[index] = uploaded;
        }
      } catch (error) {
        // Mark as failed
        const index = newAttachments.indexOf(pendingAttachment);
        if (index !== -1) {
          newAttachments[index] = {
            ...pendingAttachment,
            status: 'error',
            error: error instanceof Error ? error.message : 'Upload failed',
          };
        }
      }
    }

    onAttachmentsChange([...attachments, ...newAttachments]);
    setIsOpen(false);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [attachments, onAttachmentsChange, uploadFile]);

  const handleRemoveAttachment = useCallback((id: string) => {
    onAttachmentsChange(attachments.filter((a) => a.id !== id));
  }, [attachments, onAttachmentsChange]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getAttachmentIcon = (type: FileAttachment['type']) => {
    switch (type) {
      case 'image':
        return <ImageIcon className="h-4 w-4" />;
      case 'document':
      case 'code':
        return <File className="h-4 w-4" />;
      default:
        return <Paperclip className="h-4 w-4" />;
    }
  };

  const totalSize = attachments.reduce((sum, a) => sum + a.size, 0);
  const hasPendingUploads = attachments.some((a) => a.status === 'uploading');

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <TooltipProvider>
        <Tooltip>
          <PopoverTrigger asChild>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 md:h-9 md:w-9 shrink-0 text-muted-foreground hover:text-foreground"
                disabled={disabled}
                type="button"
              >
                {hasPendingUploads ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                ) : (
                  <Paperclip className="h-4 w-4 md:h-5 md:w-5" />
                )}
                <span className="sr-only">Attach files</span>
              </Button>
            </TooltipTrigger>
          </PopoverTrigger>
          <TooltipContent side="top">
            <p>Attach files</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <PopoverContent
        className="w-80 p-4"
        side="top"
        align="end"
      >
        <div className="space-y-3">
          <div>
            <h4 className="text-sm font-semibold">Attachments</h4>
            <p className="text-xs text-muted-foreground mt-1">
              Upload files to include with your message
            </p>
          </div>

          {attachments.length > 0 && (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {attachments.map((attachment) => (
                <div
                  key={attachment.id}
                  className={`flex items-center gap-2 p-2 rounded-md border ${
                    attachment.status === 'uploaded'
                      ? 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-900'
                      : attachment.status === 'error'
                      ? 'bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-900'
                      : 'bg-muted/50 border-border'
                  }`}
                >
                  {attachment.preview ? (
                    <img
                      src={attachment.preview}
                      alt={attachment.name}
                      className="h-8 w-8 rounded object-cover"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded bg-muted flex items-center justify-center text-muted-foreground">
                      {getAttachmentIcon(attachment.type)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{attachment.name}</p>
                    <p className={`text-xs ${
                      attachment.status === 'uploaded'
                        ? 'text-green-700 dark:text-green-400'
                        : attachment.status === 'error'
                        ? 'text-red-700 dark:text-red-400'
                        : 'text-muted-foreground'
                    }`}>
                      {formatFileSize(attachment.size)}
                      {attachment.status === 'uploading' && ' (uploading...)'}
                      {attachment.status === 'uploaded' && ' ✓ Uploaded'}
                      {attachment.status === 'error' && ` - Failed: ${attachment.error || 'Unknown error'}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {attachment.status === 'uploading' && (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                    {attachment.status === 'uploaded' && (
                      <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                    )}
                    {attachment.status === 'error' && (
                      <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0"
                      onClick={() => handleRemoveAttachment(attachment.id)}
                      disabled={attachment.status === 'uploading'}
                      type="button"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              ))}
              {attachments.length > 0 && (
                <p className="text-xs text-muted-foreground pt-1">
                  Total: {formatFileSize(totalSize)}
                </p>
              )}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileSelect}
            // Accept Excel and CSV files for status tracker
            accept=".xlsx,.xls,.csv,.image/*,.pdf,.doc,.docx,.txt,.md,.json,.js,.ts,.tsx,.jsx"
          />

          <Button
            onClick={() => fileInputRef.current?.click()}
            className="w-full"
            size="sm"
            disabled={disabled || hasPendingUploads || !selectedSessionId}
            type="button"
          >
            <Paperclip className="h-4 w-4 mr-2" />
            Choose Files
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            Excel, CSV, images, documents, and code files supported
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
